import { normalizeLowerTrimUnderscoreToSpace, toTitleCase } from './normalize.js';
import { parseNodeProperties } from './nodeProperties.js';
/* global require */
import React from "react";
import logger from './logger.js';
import dagre from "@dagrejs/dagre";
import { getEdgeTypeColor } from "./edgeTypeColors.js";
import { MarkerType } from "@xyflow/react";
import moment from "moment";
import momentTZ from "moment-timezone";
import { CORE_NODE_TYPE_COLORS, DYNAMIC_TYPE_COLOR_PALETTE } from "./typeColorConstants.js";
import { getDagreBaseSpacing } from "./graphLayoutConfig.js";
import { ROLE_ADMIN, ROLE_APPROVER, ROLE_EDITOR } from "../constants/appConstants.js";

const nodeWidth = 172;
const nodeHeight = 62;

export const highlightMatch = (text = "", query = "") => {
  if (!query?.trim()) {
    return text;
  }
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedQuery})`, "ig");
  const parts = text.split(regex);
  if (parts.length === 1) {
    return text;
  }
  return parts.map((part, index) => (index % 2 === 1 ? <strong key={`${part}-${index}`}>{part}</strong> : part));
};

export const MIN_AUTOSUGGESTION_CHARACTERS = 3;

export const shouldFetchSuggestions = ({
  allowSuggestions = true,
  isPaused = false,
  searchTerm = "",
  hasUserTyped = true,
  minChars = MIN_AUTOSUGGESTION_CHARACTERS,
} = {}) => {
  if (!allowSuggestions || isPaused || !hasUserTyped) {
    return false;
  }
  return searchTerm?.trim().length >= minChars;
};

export const getSuggestionStatus = ({
  value = "",
  isLoading = false,
  minChars = MIN_AUTOSUGGESTION_CHARACTERS,
  loadingMessage = "Fetching suggestions...",
  emptyMessage = "No suggestions to show",
} = {}) => {
  const trimmedValue = value?.trim() || "";
  const hasMinimumChars = trimmedValue.length >= minChars;

  let statusMessage;
  if (!hasMinimumChars) {
    statusMessage = `Type at least ${minChars} characters`;
  } else if (isLoading) {
    statusMessage = loadingMessage;
  } else {
    statusMessage = emptyMessage;
  }

  return {
    trimmedValue,
    hasMinimumChars,
    statusMessage,
  };
};

/**
 * Parse properties that may be a JSON string or already an object
 */
export const parseProperties = (rawProperties) => {
  if (!rawProperties) {
    return null;
  }
  if (typeof rawProperties === "string") {
    try {
      const parsed = JSON.parse(rawProperties);
      return parseNodeProperties(parsed);
    } catch (error) {
      logger.warn("[formatAutoSuggestionOptions] Unable to parse properties string:", error);
      return null;
    }
  }
  if (typeof rawProperties === "object") {
    return parseNodeProperties(rawProperties);
  }
  return null;
};

export const formatAutoSuggestionOptions = (suggestions = [], { mode = "object", type = "autosuggestion" } = {}) => {
  if (!Array.isArray(suggestions) || suggestions.length === 0) {
    return [];
  }

  return suggestions.map((item, index) => {
    const label = item?.name || "";
    if (mode === "string") {
      return label;
    }

    return {
      label,
      name: item?.name || "", // Preserve original name for node name resolution
      id: item?.id,
      type,
      isFirst: index === 0,
      properties: parseProperties(item?.properties),
      infoLabel: item?.label || "",
    };
  });
};

export const getSuggestionDropdownMetrics = ({
  optionCount = 0,
  maxHeight = 200,
  rowHeight = 40,
  minRows = 1,
  extraContentHeight = 0,
} = {}) => {
  const effectiveRows = optionCount > 0 ? Math.max(optionCount, minRows) : 0;
  const contentHeight = effectiveRows * rowHeight + extraContentHeight;
  const containerMinHeight = contentHeight > 0 ? Math.min(maxHeight, contentHeight) : undefined;

  return {
    containerMinHeight,
    listMaxHeight: maxHeight,
  };
};

export const getSuggestionDropdownState = ({
  optionsLength = 0,
  isLoading = false,
  baseOpen = true,
  hasUserTyped = true,
  allowWithoutTyping = false,
  dropdownConfig = {},
} = {}) => {
  const hasOptions = optionsLength > 0 && (hasUserTyped || allowWithoutTyping);
  const metrics = getSuggestionDropdownMetrics({
    optionCount: optionsLength,
    ...dropdownConfig,
  });

  return {
    hasOptions,
    isOpen: Boolean(baseOpen && hasOptions && !isLoading),
    metrics,
  };
};

export const createSuggestionController = (setPauseFn = () => { }) => {
  const safeSetter = typeof setPauseFn === "function" ? setPauseFn : () => { };
  return {
    handleInputReason: (reason) => {
      if (!reason) {
        return;
      }
      if (reason === "input") {
        safeSetter(false);
      } else if (reason === "reset") {
        safeSetter(true);
      }
    },
    handleSelectionReason: (reason) => {
      if (reason === "selectOption") {
        safeSetter(true);
      }
    },
    pause: () => safeSetter(true),
    resume: () => safeSetter(false),
  };
};

export const getLayoutedElements = (nodes, edges, direction = "TB") => {
  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  const isHorizontal = direction === "LR";
  const { nodesep, ranksep } = getDagreBaseSpacing(direction);

  dagreGraph.setGraph({
    rankdir: direction,
    nodesep, // Vertical spacing between nodes (perpendicular to flow)
    ranksep, // Horizontal spacing between ranks (along flow direction)
    edgesep: 20, // Minimum edge separation
  });

  // This is going to dynamically set the horizontal / vertical space between a node and its inward / outward node, based on n umber of inward/ outward nodes
  const outwardChildCountMap = new Map();
  const inwardChildCountMap = new Map();
  const spacingInfoMap = new Map();
  edges.forEach((edge) => {
    if (edge.hidden || edge?.data?.skipLayout) return;
    outwardChildCountMap.set(edge.source, (outwardChildCountMap.get(edge.source) || 0) + 1);
    inwardChildCountMap.set(edge.target, (inwardChildCountMap.get(edge.target) || 0) + 1);
  });
  nodes.forEach((node) => {
    const preservePosition = Boolean(node?.data?.preservePosition);
    if (!node.hidden && !preservePosition) {
      const outwardCount = outwardChildCountMap.get(node.id) || 0;
      const inwardCount = inwardChildCountMap.get(node.id) || 0;
      const maxConnections = Math.max(outwardCount, inwardCount);
      const additionalSpacing = maxConnections > 1 ? Math.min((maxConnections - 1) * 5, 150) : 0;

      const outwardSpacing = outwardCount > 1 ? Math.min((outwardCount - 1) * 5, 150) : 0;
      const inwardSpacing = inwardCount > 1 ? Math.min((inwardCount - 1) * 5, 150) : 0;
      spacingInfoMap.set(node.id, { outwardSpacing, inwardSpacing });

      const layoutWidth = isHorizontal ? nodeWidth + additionalSpacing : nodeWidth;
      const layoutHeight = !isHorizontal ? nodeHeight + additionalSpacing : nodeHeight;
      dagreGraph.setNode(node.id, { width: layoutWidth, height: layoutHeight });
    } else {
      spacingInfoMap.set(node.id, { outwardSpacing: 0, inwardSpacing: 0 });
    }
  });

  edges.forEach((edge) => {
    if (!edge.hidden && !edge?.data?.skipLayout && dagreGraph.hasNode(edge.source) && dagreGraph.hasNode(edge.target)) {
      dagreGraph.setEdge(edge.source, edge.target);
    }
  });
  dagre.layout(dagreGraph);
  const newNodes = nodes.map((node) => {
    if (node.hidden) {
      return {
        ...node,
        targetPosition: isHorizontal ? "left" : "top",
        sourcePosition: isHorizontal ? "right" : "bottom",
        position: {
          x: -1000,
          y: -1000,
        },
        width: nodeWidth,
        height: nodeHeight,
        data: {
          ...node.data,
          position: {
            x: -1000,
            y: -1000,
          },
        },
      };
    }
    if (node?.data?.preservePosition || !dagreGraph.hasNode(node.id)) {
      const preservedPosition = {
        x: node.position?.x ?? 0,
        y: node.position?.y ?? 0,
      };
      return {
        ...node,
        targetPosition: isHorizontal ? "left" : "top",
        sourcePosition: isHorizontal ? "right" : "bottom",
        width: nodeWidth,
        height: nodeHeight,
        position: preservedPosition,
        data: {
          ...node.data,
          position: preservedPosition,
        },
      };
    }
    const nodeWithPosition = dagreGraph.node(node.id);
    const { outwardSpacing = 0, inwardSpacing = 0 } = spacingInfoMap.get(node.id) || {};
    const shift = (inwardSpacing - outwardSpacing) / 2;
    return {
      ...node,
      targetPosition: isHorizontal ? "left" : "top",
      sourcePosition: isHorizontal ? "right" : "bottom",
      position: {
        x: isHorizontal ? nodeWithPosition.x - nodeWidth / 2 + shift : nodeWithPosition.x - nodeWidth / 2,
        y: !isHorizontal ? nodeWithPosition.y - nodeHeight / 2 + shift : nodeWithPosition.y - nodeHeight / 2,
      },
      width: nodeWidth,
      height: nodeHeight,
      data: {
        ...node.data,
        position: {
          x: isHorizontal ? nodeWithPosition.x - nodeWidth / 2 + shift : nodeWithPosition.x - nodeWidth / 2,
          y: !isHorizontal ? nodeWithPosition.y - nodeHeight / 2 + shift : nodeWithPosition.y - nodeHeight / 2,
        },
      },
    };
  });

  return { nodes: newNodes, edges };
};

// Wraps an already-laid-out (LR) linear graph into a boustrophedon "snake" so a
// long horizontal chain is folded across multiple rows and fits the viewport
// width while making use of vertical space. Intended for the initial process
// flow graph only. Returns the same node list with repositioned visible nodes.
export const applySerpentineLayout = (nodes, options = {}) => {
  const {
    viewportWidth = 0,
    viewportHeight = 0,
    columnGap = 60,
    rowGap = 120,
  } = options;

  const visibleNodes = (nodes || []).filter(
    (node) => !node.hidden && node.position && node.position.x > -900
  );
  if (visibleNodes.length <= 1) return nodes;

  // Group visible nodes into ranks by their laid-out x (dagre columns), so any
  // parallel branch nodes sharing a column stay together vertically.
  const RANK_TOLERANCE = 1;
  const rankBuckets = [];
  [...visibleNodes]
    .sort((a, b) => a.position.x - b.position.x)
    .forEach((node) => {
      const lastBucket = rankBuckets[rankBuckets.length - 1];
      if (lastBucket && Math.abs(lastBucket.x - node.position.x) <= RANK_TOLERANCE) {
        lastBucket.nodes.push(node);
      } else {
        rankBuckets.push({ x: node.position.x, nodes: [node] });
      }
    });

  const rankCount = rankBuckets.length;
  if (rankCount <= 1) return nodes;

  const columnStep = nodeWidth + columnGap;

  // Uniform row height from the tallest rank so wrapped rows never collide.
  let maxRankHeight = nodeHeight;
  rankBuckets.forEach((bucket) => {
    const minY = Math.min(...bucket.nodes.map((n) => n.position.y));
    const maxY = Math.max(
      ...bucket.nodes.map((n) => n.position.y + (n.height || nodeHeight))
    );
    bucket.minY = minY;
    maxRankHeight = Math.max(maxRankHeight, maxY - minY);
  });
  const rowStep = maxRankHeight + rowGap;

  // Pick columns-per-row so the folded block roughly matches the viewport aspect
  // ratio, spreading the chain across vertical space instead of one long line.
  const aspect =
    viewportWidth > 0 && viewportHeight > 0 ? viewportWidth / viewportHeight : 16 / 9;
  const balancedCols = Math.round(
    Math.sqrt((aspect * rankCount * rowStep) / columnStep)
  );
  const columnsPerRow = Math.max(1, Math.min(rankCount, balancedCols || 1));

  const positionById = new Map();
  rankBuckets.forEach((bucket, rankIndex) => {
    const rowIndex = Math.floor(rankIndex / columnsPerRow);
    const indexInRow = rankIndex % columnsPerRow;
    // Even rows flow left→right, odd rows right→left (snake).
    const columnInRow =
      rowIndex % 2 === 0 ? indexInRow : columnsPerRow - 1 - indexInRow;
    const baseX = columnInRow * columnStep;
    const baseY = rowIndex * rowStep;
    bucket.nodes.forEach((node) => {
      positionById.set(node.id, {
        x: baseX,
        y: baseY + (node.position.y - bucket.minY),
      });
    });
  });

  return nodes.map((node) => {
    const nextPosition = positionById.get(node.id);
    if (!nextPosition) return node;
    return {
      ...node,
      position: nextPosition,
      data: { ...node.data, position: nextPosition },
    };
  });
};

export const getDynamicPosition = (parentNode, newNodes, existingNodes) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "LR", nodesep: 50, ranksep: 50 });
  g.setDefaultEdgeLabel(() => ({}));
  g.setNode(parentNode.id, { width: nodeWidth, height: nodeHeight, x: parentNode.position.x, y: parentNode.position.y });
  newNodes.forEach((node) => {
    g.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    g.setEdge(parentNode.id, node.id);
  });
  dagre.layout(g);
  let positionedNodes = newNodes.map((node) => {
    const pos = g.node(node.id);

    return {
      ...node,
      targetPosition: "left",
      width: nodeWidth,
      height: nodeHeight,
      sourcePosition: "right",
      position: {
        x: pos.x + parentNode.position.x,
        y: pos.y + parentNode.position.y,
      },
      positionAbsolute: true,
      data: {
        ...node.data,
        position: {
          x: pos.x + parentNode.position.x,
          y: pos.y + parentNode.position.y,
        },
        sourceNodeId: parentNode.id,
      },
    };
  });
  positionedNodes = adjustPositions(positionedNodes, existingNodes);
  return positionedNodes;
};
const isOverlapping = (nodeA, nodeB) => {
  return (
    nodeA.position.x < nodeB.position.x + (nodeB.width || nodeWidth) &&
    nodeA.position.x + (nodeA.width || nodeWidth) > nodeB.position.x &&
    nodeA.position.y < nodeB.position.y + (nodeB.height || nodeHeight) &&
    nodeA.position.y + (nodeA.height || nodeHeight) > nodeB.position.y
  );
};
const adjustPositions = (newNodes, existingNodes, delta = 20) => {
  return newNodes.map((node) => {
    let adjustedNode = { ...node };
    let overlapFound = true;
    while (overlapFound) {
      overlapFound = false;
      for (const existingNode of existingNodes) {
        if (isOverlapping(adjustedNode, existingNode)) {
          adjustedNode = {
            ...adjustedNode,
            position: { ...adjustedNode.position, y: adjustedNode.position.y + delta },
          };
          overlapFound = true;
          break;
        }
      }
    }
    return adjustedNode;
  });
};

const DEFAULT_EXCLUDED_NODE_TYPES = new Set(["attribute"]);

export const SUPPLY_CHAIN_GROUP_KEY = "labels";

export const SUPPLY_CHAIN_LABEL = "Supply Chain Layers";

export const isSupplyChainGroupKey = (key = "") => {
  const normalized = String(key || "").trim().toLowerCase();
  return normalized === "labels" || normalized === "supply_chain_layers" || normalized === "supply chain layers";
};

export const isSupplyChainFilterItem = (item = {}, defaultGroupKey = SUPPLY_CHAIN_GROUP_KEY) =>
  isSupplyChainGroupKey(item?.groupKey || defaultGroupKey);

export const isAttributeFilterItem = (
  item = {},
  { attributeKey = "attribute", defaultGroupKey = SUPPLY_CHAIN_GROUP_KEY } = {}
) => (
  isSupplyChainGroupKey(item?.groupKey || defaultGroupKey)
  && String(item?.name || "").toLowerCase() === attributeKey
);

export const getFilterGroupLabel = (key = "") => String(key ?? "").trim();

export const buildGroupedFilters = (items = [], { defaultGroupKey = SUPPLY_CHAIN_GROUP_KEY } = {}) => {
  if (!Array.isArray(items)) return [];
  const groupOrder = [];
  const grouped = new Map();
  items.forEach((item = {}) => {
    const groupKey = item?.groupKey || defaultGroupKey;
    const groupLabel = item?.groupLabel || getFilterGroupLabel(groupKey);
    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, { key: groupKey, label: groupLabel, items: [] });
      groupOrder.push(groupKey);
    }
    grouped.get(groupKey).items.push(item);
  });
  return groupOrder.map((key) => grouped.get(key));
};

export const formatFilterGroupHeading = (groupKey, label) => (
  isSupplyChainGroupKey(groupKey)
    ? SUPPLY_CHAIN_LABEL
    : (String(groupKey || "").trim().toLowerCase() === "flow_type"
      || String(groupKey || "").trim().toLowerCase() === "process_nature")
      ? "Process Nature"
      : toTitleCase(label)
);

export const formatFilterValue = (item = {}) => (
  isSupplyChainGroupKey(item?.groupKey || SUPPLY_CHAIN_GROUP_KEY)
    ? toTitleCase(item?.name)
    : String(item?.name ?? "")
);

export const shouldShowNodeTypeByDefault = (label = "") =>
  !DEFAULT_EXCLUDED_NODE_TYPES.has(String(label || "").toLowerCase());

export const shouldShowFilterByDefault = (label = "", groupKey = "") =>
  (isSupplyChainGroupKey(groupKey) ? shouldShowNodeTypeByDefault(label) : false);

export const resolvePropertyGroupValue = (properties, groupKey) => {
  if (!properties || typeof properties !== "object") return undefined;
  if (Object.prototype.hasOwnProperty.call(properties, groupKey)) return properties[groupKey];
  const matchKey = Object.keys(properties).find(
    (key) => String(key).toLowerCase() === String(groupKey).toLowerCase()
  );
  return matchKey ? properties[matchKey] : undefined;
};

export const matchesPropertyGroupSelection = (properties, groupKey, values) => {
  if (!values || values.size === 0) return true;
  const groupValue = resolvePropertyGroupValue(properties, groupKey);
  if (groupValue == null) return false;

  const normalizeSelectionValue = (value) => String(value ?? "").trim().toLowerCase();
  const selectedValues = Array.from(values)
    .map((value) => normalizeSelectionValue(value))
    .filter(Boolean);
  if (!selectedValues.length) return true;

  const parseDelimitedValues = (value) => String(value)
    .split(',')
    .map((entry) => normalizeSelectionValue(entry))
    .filter(Boolean);

  if (Array.isArray(groupValue)) {
    const normalizedValues = groupValue.flatMap((value) => (
      typeof value === 'string' ? parseDelimitedValues(value) : [normalizeSelectionValue(value)]
    ));
    const groupSet = new Set(normalizedValues.filter(Boolean));
    return selectedValues.some((value) => groupSet.has(value));
  }

  if (typeof groupValue === "object") {
    const valueKeys = Object.keys(groupValue);
    if (!valueKeys.length) return false;
    const lowerKeyMap = new Map(valueKeys.map((key) => [String(key).toLowerCase(), key]));
    return selectedValues.some((value) => {
      if (Object.prototype.hasOwnProperty.call(groupValue, value)) {
        return Boolean(groupValue[value]);
      }
      const lowered = String(value).toLowerCase();
      const matchedKey = lowerKeyMap.get(lowered);
      return matchedKey ? Boolean(groupValue[matchedKey]) : false;
    });
  }

  const normalizedGroupValues = parseDelimitedValues(groupValue);
  return selectedValues.some((value) => normalizedGroupValues.includes(value));
};

export const moveAttributeFilterToEnd = (filters = []) => {
  const list = Array.isArray(filters) ? [...filters] : [];
  const hasGroupKey = list.some((item = {}) => item?.groupKey);
  if (!hasGroupKey) {
    const attrIndex = list.findIndex((item = {}) => String(item?.name || "").toLowerCase() === "attribute");
    if (attrIndex >= 0 && attrIndex < list.length - 1) {
      const [attr] = list.splice(attrIndex, 1);
      list.push(attr);
    }
    return list;
  }

  const groupOrder = [];
  const grouped = new Map();
  list.forEach((item = {}) => {
    const groupKey = item?.groupKey || SUPPLY_CHAIN_GROUP_KEY;
    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, []);
      groupOrder.push(groupKey);
    }
    grouped.get(groupKey).push(item);
  });

  const flattened = [];
  groupOrder.forEach((groupKey) => {
    const items = grouped.get(groupKey) || [];
    if (isSupplyChainGroupKey(groupKey)) {
      const attrIndex = items.findIndex((item = {}) => String(item?.name || "").toLowerCase() === "attribute");
      if (attrIndex >= 0 && attrIndex < items.length - 1) {
        const [attr] = items.splice(attrIndex, 1);
        items.push(attr);
      }
    }
    flattened.push(...items);
  });

  return flattened;
};

export const parseFilterData = (apiResponse) => {
  if (!apiResponse) {
    return [];
  }

  const buildItems = (values = [], groupKey = SUPPLY_CHAIN_GROUP_KEY) =>
    (Array.isArray(values) ? values : []).map((filterLabel, index) => {
      const rawLabel = filterLabel == null ? "" : String(filterLabel).trim();
      return {
        name: rawLabel,
        id: `${groupKey}-${index}`,
        groupKey,
        groupLabel: getFilterGroupLabel(groupKey),
        isChecked: shouldShowFilterByDefault(rawLabel, groupKey),
        color: getNodeColor(rawLabel),
      };
    });

  if (Array.isArray(apiResponse)) {
    const parsed = buildItems(apiResponse, SUPPLY_CHAIN_GROUP_KEY);
    return moveAttributeFilterToEnd(parsed);
  }

  if (typeof apiResponse === "object") {
    const entries = Object.entries(apiResponse)
      .filter(([key]) => String(key).toLowerCase() !== "error")
      .filter(([, value]) => Array.isArray(value) && value.length);

    const ordered = [];
    const labelsEntry = entries.find(([key]) => isSupplyChainGroupKey(key));
    if (labelsEntry) {
      ordered.push(labelsEntry);
    }
    entries.forEach((entry) => {
      if (labelsEntry && entry[0] === labelsEntry[0]) return;
      ordered.push(entry);
    });

    const parsed = ordered.flatMap(([key, values]) => buildItems(values, key));
    return moveAttributeFilterToEnd(parsed);
  }

  return [];
};

// Keep original local name for backwards compatibility with existing logic.
const predefinedColors = DYNAMIC_TYPE_COLOR_PALETTE;

const nodeColorMap = new Map();
const assignDynamicColor = (key) => {
  if (nodeColorMap.has(key)) {
    return nodeColorMap.get(key);
  }

  const usedColors = new Set(nodeColorMap.values());
  const availableColor = predefinedColors.find((color) => !usedColors.has(color));
  let chosenColor = availableColor;
  if (!chosenColor) {
    // Use crypto.getRandomValues for security instead of Math.random
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const index = array[0] % predefinedColors.length;
    chosenColor = predefinedColors[index];
  }
  nodeColorMap.set(key, chosenColor);
  return chosenColor;
};
export const getNodeColor = (nodeLabel) => {
  const rawKey = String(nodeLabel ?? "").trim().toLowerCase();
  const coreKey = rawKey.replace(/\s+/g, "_");
  return CORE_NODE_TYPE_COLORS[coreKey] || assignDynamicColor(rawKey);
};
export const getOpaqColor = (hex) => {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  // Use a very low alpha to keep node backgrounds ultra-light.
  return `rgba(${r}, ${g}, ${b},0.08)`;
};

const parseNestedJson = (value) => {
  if (!value) {
    return null;
  }
  let parsed = value;
  for (let depth = 0; depth < 2; depth += 1) {
    if (typeof parsed !== "string") {
      break;
    }
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  return parsed && typeof parsed === "object" ? parsed : null;
};

// Max number of attribute nodes allowed to be connected to a single node.
export const MAX_ATTRIBUTE_NODES_PER_NODE = 6;

// Removes excess "attribute" nodes (and their edges) so that any node has at
// most MAX_ATTRIBUTE_NODES_PER_NODE attribute nodes connected to it.
export const limitAttributeNodesPerNode = (nodes = [], edges = []) => {
  if (!Array.isArray(nodes) || !Array.isArray(edges) || !nodes.length) {
    return { nodes: nodes || [], edges: edges || [] };
  }

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const isAttribute = (node) =>
    String(node?.data?.nodeType ?? node?.nodeType ?? "").toLowerCase().trim() === "attribute";

  const attributeCountByNeighbor = new Map();
  const removedAttributeNodeIds = new Set();

  const keptEdges = edges.filter((edge) => {
    const sourceNode = nodeById.get(edge.source);
    const targetNode = nodeById.get(edge.target);
    const sourceIsAttr = isAttribute(sourceNode);
    const targetIsAttr = isAttribute(targetNode);

    // Only constrain edges between an attribute node and a non-attribute node.
    if (sourceIsAttr === targetIsAttr) {
      return true;
    }

    const attributeNodeId = sourceIsAttr ? edge.source : edge.target;
    const anchorNodeId = sourceIsAttr ? edge.target : edge.source;

    // Attribute node already removed for this anchor: drop the edge too.
    if (removedAttributeNodeIds.has(attributeNodeId)) {
      return false;
    }

    const currentCount = attributeCountByNeighbor.get(anchorNodeId) || 0;
    if (currentCount >= MAX_ATTRIBUTE_NODES_PER_NODE) {
      removedAttributeNodeIds.add(attributeNodeId);
      return false;
    }

    attributeCountByNeighbor.set(anchorNodeId, currentCount + 1);
    return true;
  });

  // Keep an attribute node only if it still has at least one surviving edge.
  const connectedNodeIds = new Set();
  keptEdges.forEach((edge) => {
    connectedNodeIds.add(edge.source);
    connectedNodeIds.add(edge.target);
  });

  const keptNodes = nodes.filter((node) => {
    if (!isAttribute(node)) {
      return true;
    }
    if (removedAttributeNodeIds.has(node.id)) {
      return false;
    }
    return connectedNodeIds.has(node.id);
  });

  return { nodes: keptNodes, edges: keptEdges };
};

export const parseGraphData = (apiResponse, isExpandedData = false) => {
  if (!apiResponse?.nodes || !apiResponse?.edges) {
    return { nodes: [], edges: [] };
  }const nodes = apiResponse.nodes.map((node) => {
    const properties = parseNodeProperties(node?.properties);
    const displayName = properties?.name || node?.label || "";
    delete properties?.name;
    delete properties["name:String"];

    const additonalEdCount = parseNestedJson(node?.additionalEdgeCount) || {};
    const additionalEdgeCountEntities = parseNestedJson(node?.additionalEdgeCountEntities);

    const rawNodeType = node?.label ? String(node?.label) : "";
    const nodeType = rawNodeType ? rawNodeType.replace(/_/g, " ") : "";
    const color = getNodeColor(nodeType);
    return {
      id: node?.id,
      label: displayName,
      type: "customNode",
      properties: properties,
      hidden: false,
      data: {
        id: node?.id,
        label: displayName,
        nodeType,
        isStacked: additonalEdCount?.outward > 0 || additonalEdCount?.inward > 0,
        isExpanded: false,
        additionalOutwardEdgeCount: additonalEdCount?.outward,
        additionalInwardEdgeCount: additonalEdCount?.inward,
        color: color,
        opaqColor: getOpaqColor(color),
        timeStamp: new Date().getTime(),
        isOutwardExpanded: false,
        isInwardExpanded: false,
        isExpandedData: isExpandedData,
        additionalEdgeCountEntities,
      },
    };
  });

  const edges = apiResponse.edges.map((edge) => {
    const edgeColor = getEdgeTypeColor(edge.label);
    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label,
      hidden: false,
      markerEnd: {
        type: MarkerType.Arrow,
        color: edgeColor,
      },
      type: "customEdge",
      style: { backgroundColor: edgeColor, color: "white", stroke: edgeColor },
      data: {
        label: edge.label,
        color: "white",
        labelColor: edgeColor,
        isExpandedData: isExpandedData,
      },
      timeStamp: new Date().getTime(),
    };
  });

  return limitAttributeNodesPerNode(nodes, edges);
};

export const parseNodeData = (apiData) => {
  if (!apiData?.node) {
    return null;
  }
  const property = parseNodeProperties(apiData?.node?.properties);
  return {
    id: apiData?.node?.id,
    nodeType: apiData.node.label,
    type: "customNode",
    properties: property,
    data: {
      id: apiData?.node?.id,
      label: property?.name || "",
    },
  };
};
const BASE_EDGE_STYLE = {
  stroke: "#303132",
  backgroundColor: "#303132",
  color: "white",
};

const BASE_MARKER = {
  type: MarkerType.Arrow,
  color: "#303132",
};

const applyDefaultEdgeVisuals = (edge = {}) => ({
  ...edge,
  style: (() => {
    const label = edge?.label || edge?.data?.label;
    const typeColor = getEdgeTypeColor(label);
    const next = {
      ...BASE_EDGE_STYLE,
      ...(edge.style || {}),
      stroke: (edge.style || {})?.stroke || typeColor,
      backgroundColor: (edge.style || {})?.backgroundColor || typeColor,
    };
    return next;
  })(),
  markerEnd: (() => {
    const label = edge?.label || edge?.data?.label;
    const typeColor = getEdgeTypeColor(label);
    const next = {
      ...BASE_MARKER,
      ...(edge.markerEnd || {}),
      color: (edge.markerEnd || {})?.color || typeColor,
    };
    return next;
  })(),
  data: {
    ...(edge.data || {}),
    labelColor: edge?.data?.labelColor || edge?.style?.backgroundColor || edge?.style?.stroke || getEdgeTypeColor(edge?.label || edge?.data?.label),
  },
});

export const updateNodesAndEdges = (prevNodes, prevEdges, newData) => {
  const nodeMap = new Map();
  prevNodes.forEach((node) => nodeMap.set(node.id, node));

  newData.nodes.forEach((newNode) => {
    if (nodeMap.has(newNode.id)) {
      nodeMap.set(newNode.id, { ...nodeMap.get(newNode.id), ...newNode });
    } else {
      nodeMap.set(newNode.id, newNode);
    }
  });
  const updatedNodes = Array.from(nodeMap.values());

  const edgeMap = new Map();
  prevEdges.forEach((edge) => {
    const key = `${edge.source}-${edge.target}`;
    edgeMap.set(key, edge);
  });
  newData.edges.forEach((newEdge) => {
    const key = `${newEdge.source}-${newEdge.target}`;
    const newEdgeWithStyle = applyDefaultEdgeVisuals(newEdge);
    if (edgeMap.has(key)) {
      edgeMap.set(key, { ...edgeMap.get(key), ...newEdgeWithStyle });
    } else {
      edgeMap.set(key, newEdgeWithStyle);
    }
  });
  const updatedEdges = Array.from(edgeMap.values());
  return { updatedNodes, updatedEdges };
};
export const getNewElementLayout = (oldElements, newElements) => {
  const existingNodeIds = new Set(oldElements?.nodes.map((node) => node.id));
  const existingEdgeSet = new Set(oldElements?.edges.map((edge) => edge.id));

  const filteredNewNodes = newElements.nodes.filter((node) => !existingNodeIds.has(node.id));
  const filteredNewEdges = newElements.edges.filter((edge) => !existingEdgeSet.has(edge.id)).map(applyDefaultEdgeVisuals);

  if (filteredNewNodes?.length === 0 && filteredNewEdges?.length === 0) {
    return oldElements;
  }

  const { nodes: layoutedNewNodes, edges: layoutedNewEdges } = getLayoutedElements(filteredNewNodes, filteredNewEdges, "LR");
  const newNodes = layoutedNewNodes.map((node) => ({
    ...node,
    // position:{x:node.position.x+rightMostX+50, y:node.position.y},
    position: getNonOverlappingPosition(node, layoutedNewNodes),
    data: {
      ...node.data,
      position: getNonOverlappingPosition(node, layoutedNewNodes),
    },
  }));

  return { nodes: [...oldElements.nodes, ...newNodes], edges: [oldElements?.edges, ...layoutedNewEdges] };
};
const getNonOverlappingPosition = (node, existingNodes) => {
  const padding = 50;
  let newX = node.position.x;
  let newY = node.position.y;
  while (existingNodes.some((n) => Math.abs(n.position.x - newX) < padding && Math.abs(n.position.y - newY) < padding)) {
    newX += padding;
    newY += padding / 2;
  }
  return { x: newX, y: newY };
};

export const collapseParentNodeRecursive = (parentId, nodes, edges, currentChildId = null, direction = "outward") => {
  let updatedNodes = [...nodes];
  let updatedEdges = [...edges];

  const directEdges =
    direction === "inward"
      ? updatedEdges.filter((edge) => edge.target === parentId)
      : updatedEdges.filter((edge) => edge.source === parentId);

  directEdges.forEach((edge) => {
    const nodeToCollapseId = direction === "inward" ? edge.source : edge.target;

    if (currentChildId && nodeToCollapseId === currentChildId) {
      return;
    }

    const nodeToCollapse = updatedNodes.find((n) => n.id === nodeToCollapseId);

    if (!nodeToCollapse) {
      return;
    }

    const checkConnections =
      direction === "inward"
        ? updatedEdges.filter((e) => e.source === nodeToCollapseId)
        : updatedEdges.filter((e) => e.target === nodeToCollapseId);

    const isShared =
      direction === "inward"
        ? checkConnections.some((e) => e.target !== parentId)
        : checkConnections.some((e) => e.source !== parentId);

    updatedEdges =
      direction === "inward"
        ? updatedEdges.filter((e) => !(e.source === nodeToCollapseId && e.target === parentId))
        : updatedEdges.filter((e) => !(e.source === parentId && e.target === nodeToCollapseId));

    if (!isShared) {
      updatedNodes = updatedNodes.filter((n) => n.id !== nodeToCollapseId);

      const result = collapseParentNodeRecursive(nodeToCollapseId, updatedNodes, updatedEdges, null, direction);
      updatedNodes = result.updatedNodes;
      updatedEdges = result.updatedEdges;
    }
  });

  return { updatedNodes, updatedEdges };
};

export const performDataFiltering = (nodeArray, edgeArray, filters) => {
  const filterDataNodes = [...nodeArray];
  const filterDataEdges = [...edgeArray];
  let newNodes = [];
  let newEdges = [];

  if (!Array.isArray(filters) || filters.length === 0) {
    newNodes = filterDataNodes.map((node) => ({ ...node, hidden: false }));
    newEdges = filterDataEdges.map((edge) => ({ ...edge, hidden: false }));
  } else {
    const selections = new Map();
    filters.forEach((filter) => {
      if (filter == null) return;
      if (typeof filter === "object") {
        const name = String(filter?.name ?? "").trim();
        if (!name) return;
        const groupKey = String(filter?.groupKey || SUPPLY_CHAIN_GROUP_KEY).trim();
        if (!selections.has(groupKey)) selections.set(groupKey, new Set());
        selections.get(groupKey).add(name);
        return;
      }
      const name = String(filter).trim();
      if (!name) return;
      if (!selections.has(SUPPLY_CHAIN_GROUP_KEY)) selections.set(SUPPLY_CHAIN_GROUP_KEY, new Set());
      selections.get(SUPPLY_CHAIN_GROUP_KEY).add(name);
    });

    if (!selections.size) {
      newNodes = filterDataNodes.map((node) => ({ ...node, hidden: false }));
      newEdges = filterDataEdges.map((edge) => ({ ...edge, hidden: false }));
    } else {
      const labelSelections = [];
      const propertyGroups = [];

      selections.forEach((values, groupKey) => {
        if (isSupplyChainGroupKey(groupKey)) {
          values.forEach((value) => labelSelections.push(value));
        } else {
          propertyGroups.push({ groupKey, values });
        }
      });

      const labelFilterSet = new Set(labelSelections.map(normalizeLowerTrimUnderscoreToSpace));

      const hiddenLookup = new Map();
      const hasLabelFilters = labelFilterSet.size > 0;
      const hasPropertyFilters = propertyGroups.length > 0;

      newNodes = filterDataNodes.map((node) => {
        const nodeTypeValue = node?.data?.nodeType ?? node?.data?.node_type;
        const labelMatch = hasLabelFilters
          ? labelFilterSet.has(normalizeLowerTrimUnderscoreToSpace(nodeTypeValue))
          : false;

        const nodeProperties = parseProperties(node?.properties ?? node?.data?.properties);
        const propertyMatchAny = hasPropertyFilters
          ? propertyGroups.some((group) => matchesPropertyGroupSelection(nodeProperties, group.groupKey, group.values))
          : false;

        const isVisible = hasLabelFilters || hasPropertyFilters
          ? (labelMatch || propertyMatchAny)
          : true;
        const hidden = !isVisible;
        hiddenLookup.set(node.id, hidden);
        return { ...node, hidden };
      });
      newEdges = filterDataEdges.map((edge) => {
        const sourceNodeHidden = hiddenLookup.get(edge.source);
        const targetNodeHidden = hiddenLookup.get(edge.target);
        return {
          ...edge,
          hidden: !!sourceNodeHidden || !!targetNodeHidden,
        };
      });
    }
  }
  const { nodes, edges } = getLayoutedElements(newNodes, newEdges, "LR");
  return { nodes, edges };
};

export const hasGraphEditPermissions = (role) => {
  const allowed = new Set([
    String(ROLE_ADMIN).toLowerCase(),
    String(ROLE_EDITOR).toLowerCase(),
    String(ROLE_APPROVER).toLowerCase(),
  ]);

  const matchesAllowed = (value) => getRoleTokens(value).some((token) => allowed.has(token));

  if (!role) return false;
  return Array.isArray(role) ? role.some(matchesAllowed) : matchesAllowed(role);
};

export const isAdminRole = (role) => {
  const allowed = new Set([
    String(ROLE_ADMIN).toLowerCase(),
    String(ROLE_APPROVER).toLowerCase(),
  ]);

  const matchesAllowed = (value) => getRoleTokens(value).some((token) => allowed.has(token));

  if (!role) return false;
  return Array.isArray(role) ? role.some(matchesAllowed) : matchesAllowed(role);
};

const getRoleTokens = (value) => {
  if (value == null) return [];
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    const tokens = normalized.split(/[^a-z0-9]+/g).filter(Boolean);
    return [normalized, ...tokens];
  }
  if (typeof value === "object") {
    return [
      ...getRoleTokens(value.role),
      ...getRoleTokens(value.name),
      ...getRoleTokens(value.value),
    ];
  }
  return getRoleTokens(String(value));
};

export const isRestrictedViewerRole = (role) => {
  const requiredTokens = ["scx", "viewer", "restricted"];
  const hasRestrictedTokens = (value) => {
    const tokenSet = new Set(getRoleTokens(value));
    return requiredTokens.every((token) => tokenSet.has(token));
  };

  if (!role) return false;
  return Array.isArray(role) ? role.some(hasRestrictedTokens) : hasRestrictedTokens(role);
};

export const hasGraphViewPermissions = (role) => !isRestrictedViewerRole(role);

export const getNodeDetailsIcons = (key) => {
  const normalizedKey = String(key || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  const req = require.context("../assets", false, /\.svg$/);
  const images = {};
  req.keys().forEach((k) => {
    const cleanedKey = k.replace("./", "");
    images[cleanedKey] = req(k).default || req(k);
  });
  let assetName;
  switch (normalizedKey) {
    case "description":
      assetName = "Description.svg";
      break;
    case "data_type":
      assetName = "DataType.svg";
      break;
    case "sample_data":
      assetName = "SampleData.svg";
      break;
    case "mongo_location":
    case "mongo_db_location":
      assetName = "MongoDBCollection.svg";
      break;
    case "reporting_db_location":
      assetName = "ReportingDBCollection.svg";
      break;
    default:
      assetName = "Teams.svg";
      break;
  }
  return images[assetName] || "";
};
function getDescendants(nodeID, edges, visited = new Set()) {
  if (visited.has(nodeID)) {
    return [];
  }
  visited.add(nodeID);
  const children = edges.filter((edge) => edge.source === nodeID).map((edge) => edge.target);
  const allDescendants = [...children];
  children.forEach((childId) => {
    allDescendants.push(...getDescendants(childId, edges, visited));
  });
  return allDescendants;
}
export const removeNodesAndDescendants = (nodesToRemove, nodeArr, edgeArr, allEdges) => {
  const idsToRemove = new Set();
  nodesToRemove.forEach((nodeId) => {
    idsToRemove.add(nodeId);
    const descendants = getDescendants(nodeId, allEdges);
    descendants.forEach((desc) => idsToRemove.add(desc));
  });
  const updatedNodes = nodeArr.filter((node) => !idsToRemove.has(node.id));
  const updatedEdges = edgeArr.filter((edge) => !idsToRemove.has(edge.source) && !idsToRemove.has(edge.target));
  return { nodes: updatedNodes, edges: updatedEdges };
};
export const convertTime = (timestamp, dateTimeFormat) => {
  if (!timestamp) {
    return "";
  }
  const userTimeZone = momentTZ.tz.guess();
  return moment.utc(timestamp).tz(userTimeZone).format(dateTimeFormat);
};

export const performNodeTypeFiltering = (nodeArray, edgeArray, filters, direction, searchedNode) => {
  const filterDataNodes = [...nodeArray];
  const filterDataEdges = [...edgeArray];

  // Build the effective filter set: if none provided, select all node types except attribute.
  let effectiveFilters = Array.isArray(filters) ? filters.filter(Boolean) : [];
  if (!effectiveFilters.length) {
    const discoveredTypes = Array.from(new Set(filterDataNodes.map((n) => String(n?.data?.nodeType || "").toLowerCase())));
    effectiveFilters = discoveredTypes.filter((type) => type && type !== "attribute");
  }

  const effectiveFilterSet = new Set(effectiveFilters.map((f) => String(f).toLowerCase()));

  // Always keep the searched node, even if its type is filtered out.
  const allowedNodeIds = new Set();

  filterDataNodes.forEach((node) => {
    const nodeType = String(node?.data?.nodeType || "").toLowerCase();
    const isSearched = searchedNode && node.id === searchedNode;
    const isAllowedType = effectiveFilterSet.has(nodeType);
    if (isAllowedType || isSearched) {
      allowedNodeIds.add(node.id);
    }
  });

  // Edge filtering: keep edges that connect the searched node to allowed neighbors in the requested direction.
  let filteredEdges = [];
  if (direction === "outward") {
    filteredEdges = filterDataEdges.filter((edge) => edge.source === searchedNode && allowedNodeIds.has(edge.target));
  } else if (direction === "inward") {
    filteredEdges = filterDataEdges.filter((edge) => edge.target === searchedNode && allowedNodeIds.has(edge.source));
  } else {
    filteredEdges = filterDataEdges.filter((edge) => allowedNodeIds.has(edge.source) || allowedNodeIds.has(edge.target));
  }

  // Ensure nodes referenced by kept edges are included; always include the searched node if present in the dataset.
  const edgeNodeIds = new Set();
  filteredEdges.forEach((edge) => {
    if (edge.source) {
      edgeNodeIds.add(edge.source);
    }
    if (edge.target) {
      edgeNodeIds.add(edge.target);
    }
  });
  if (searchedNode) {
    edgeNodeIds.add(searchedNode);
  }

  const filteredNodes = filterDataNodes.filter((node) => edgeNodeIds.has(node.id));

  const { nodes, edges } = getLayoutedElements(filteredNodes, filteredEdges, "LR");

  return { nodes, edges };
};
